

import { use, useState } from "react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material"
import { CheckCircle as AcceptIcon, Cancel as RejectIcon, Visibility as ViewIcon } from "@mui/icons-material"
import { useApiQuery, useApiMutation } from "../../hooks/useApi"
import { useAuth } from "../../contexts/AuthContext"
import { useQueryClient } from "@tanstack/react-query"

function NewJobsPage() {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [selectedJob, setSelectedJob] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [actionType, setActionType] = useState("")
  const [comments, setComments] = useState("")

  const queryClient = useQueryClient();
  const { data: newJobs, isLoading } = useApiQuery(["new-jobs"], "/jobs/new")

  // Mock data for demonstration
  const mockJobs = [
    {
      id: "J001",
      clientName: "John Doe",
      clientEmail: "john.doe@email.com",
      jobType: "ITR Filing",
      priority: "High",
      dueDate: "2024-01-25",
      description: "Individual Tax Return filing for AY 2023-24",
      estimatedFee: 2500,
      createdAt: "2024-01-15",
    },
    {
      id: "J002",
      clientName: "Jane Smith",
      clientEmail: "jane.smith@email.com",
      jobType: "GST Return",
      priority: "Medium",
      dueDate: "2024-01-30",
      description: "Monthly GST return filing",
      estimatedFee: 1500,
      createdAt: "2024-01-16",
    },
    {
      id: "J003",
      clientName: "Bob Johnson",
      clientEmail: "bob.johnson@email.com",
      jobType: "TDS Return",
      priority: "Low",
      dueDate: "2024-02-05",
      description: "Quarterly TDS return filing",
      estimatedFee: 3000,
      createdAt: "2024-01-17",
    },
  ]


  const acceptJobMutation = useApiMutation(`/jobs/${selectedJob?.JobId}/accept`, "PATCH", {
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey:"new-jobs"})
      setDialogOpen(false)
      setComments("")
    },
  })

  const rejectJobMutation = useApiMutation(`/jobs/reject`, "POST", {
    onSuccess: () => {
      setDialogOpen(false)
      setComments("")
    },
  })

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleAction = (job, action) => {
    setSelectedJob(job)
    setActionType(action)
    setDialogOpen(true)
  }

  const handleConfirmAction = () => {
    const data = {
      jobId: selectedJob.JobId,
      reason: comments,
    }

    if (actionType === "accept") {
      acceptJobMutation.mutate(data)
    } else if (actionType === "reject") {
      rejectJobMutation.mutate(data)
    }
  }

  const getPriorityColor = (priority) => {
    const colors = {
      High: "error",
      Medium: "warning",
      Low: "success",
    }
    return colors[priority] || "default"
  }

  if(isLoading) {
    return (
      <div>
        loading....
      </div>
    )
  }

  const displayJobs = newJobs || mockJobs;
  const paginatedJobs = displayJobs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        New Jobs
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        Review and accept new job assignments.
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Job ID</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Job Type</TableCell>
              {/* <TableCell>Priority</TableCell> */}
              <TableCell>Due Date</TableCell>
              {/* <TableCell>Estimated Fee</TableCell> */}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedJobs.map((job) => (
              <TableRow key={job.JobId} hover>
                <TableCell>
                  <Typography variant="subtitle2" sx={{ fontFamily: "monospace" }}>
                    {job.JobId}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2">{job.clientId.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {job.clientId.phoneNumber}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{job.jobTypeId.name}</TableCell>
                {/* <TableCell>
                  <Chip label={job?.priority} color={getPriorityColor(job?.priority)} size="small" />
                </TableCell> */}
                <TableCell>{new Date(job?.dueDate).toLocaleDateString()}</TableCell>
                {/* <TableCell>₹{job?.fees?.toLocaleString()}</TableCell> */}
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton color="primary" onClick={() => handleAction(job, "view")} size="small">
                      <ViewIcon />
                    </IconButton>
                    <IconButton color="success" onClick={() => handleAction(job, "accept")} size="small">
                      <AcceptIcon />
                    </IconButton>
                    {/* <IconButton color="error" onClick={() => handleAction(job, "reject")} size="small">
                      <RejectIcon />
                    </IconButton> */}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={displayJobs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Action Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === "accept" ? "Accept Job" : actionType === "reject" ? "Reject Job" : "Job Details"}
        </DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {selectedJob.JobId} - {selectedJob?.jobTypeId.name}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Client: {selectedJob?.clientId.name}
              </Typography>
              <Typography variant="body2" paragraph>
                {selectedJob?.description}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Due Date: {new Date(selectedJob?.dueDate)?.toLocaleDateString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Estimated Fee: ₹{selectedJob?.fees}
              </Typography>

              {(actionType === "accept" || actionType === "reject") && (
                <TextField
                  required
                  fullWidth
                  label="Comments"
                  multiline
                  rows={3}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  sx={{ mt: 2 }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          {actionType === "accept" && (
            <Button onClick={handleConfirmAction} variant="contained" color="success">
              Accept Job
            </Button>
          )}
          {/* {actionType === "reject" && (
            <Button onClick={handleConfirmAction} variant="contained" color="error">
              Reject Job
            </Button>
          )} */}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default NewJobsPage
